import { ReactNode, useEffect, useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { cn } from '@shadcn/utils';
import { useAppLayout } from 'app/utils/AppLayout';
import { Tooltip, TooltipContent, TooltipTrigger } from '@shadcn/ui';
import { LogOutIcon, PanelLeftCloseIcon, PanelLeftOpenIcon, LockIcon } from 'lucide-react';
import useJumboAuth from '@jumbo/hooks/useJumboAuth';
import projectServices, { ProjectInfoData } from 'app/services/project-services';
import { FEATURE_FLAG_EFFICIENCY_MEASURES } from 'app/utils/constants/featureFlags';
import useFeatureAccess from 'app/services/feature-access';
import { FeatureGate } from 'app/components/FeatureGate';

export type Menu = {
  label: string;
  type: 'section';
  children: {
    uri: string;
    label: string;
    type: 'nav-item';
    icon?: ReactNode;
    onClick?: (e: React.MouseEvent) => void;
  }[];
}[];

const NavItem = ({
  item,
  collapsed,
  className,
  projectId,
  isMobile,
}: {
  item: Menu[0]['children'][0];
  collapsed?: boolean;
  className?: string;
  projectId?: string;
  isMobile?: boolean;
}) => {
  const l = useLocation();
  const uri = projectId ? item.uri.replace(':id', projectId) : item.uri;
  const isActive = l.pathname === uri;

  const link = (
    <RouterLink
      to={uri}
      onClick={item.onClick}
      className={cn('flex items-center no-underline', {
        'text-primary-foreground': isActive,
        'w-full px-4 py-3': !collapsed,
        'w-full justify-center py-4': collapsed,
        'py-2': isMobile,
      })}
    >
      {item.icon && (
        <div
          className={cn('transition-transform duration-200', {
            'text-primary-foreground': isActive,
            'text-foreground/90': !isActive,
            'text-[1.5rem]': !isMobile,
            'text-[1.25rem]': isMobile,
          })}
        >
          {item.icon}
        </div>
      )}
      <span
        className={cn('ml-3 text-sm font-medium', {
          'text-primary-foreground': isActive,
          'text-foreground/90': !isActive,
          hidden: collapsed,
        })}
      >
        {item.label}
      </span>
    </RouterLink>
  );

  return (
    <div
      className={cn(
        'block w-full text-foreground/90 hover:bg-primary/10 hover:text-foreground',
        {
          'bg-primary': isActive,
        },
        className
      )}
    >
      {collapsed ? (
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>{link}</TooltipTrigger>
          <TooltipContent side="right" className="ml-2 px-2 py-1 text-sm">
            {item.label}
          </TooltipContent>
        </Tooltip>
      ) : (
        link
      )}
    </div>
  );
};

// Helper function to get required plan for a feature
const getRequiredPlanForFeature = (featureKey: string): string => {
  const planMap: Record<string, string> = {
    reportBuilder: 'Professional',
    financialInsights: 'Business',
  };
  return planMap[featureKey] || 'higher';
};

// Helper function to map menu item labels to feature keys
const getFeatureKeyForNavItem = (label: string): string | null => {
  const featureMap: Record<string, string> = {
    'Report Builder': 'reportBuilder',
    'Financial Insights': 'financialInsights',
  };
  return featureMap[label] || null;
};

const SectionItem = ({ item, isMobile }: { item: Menu[0]; isMobile?: boolean }) => {
  const { sidebarOpen } = useAppLayout();
  const location = useLocation();
  const navigate = useNavigate();
  const projectMatch = location.pathname.match(/\/project\/([^/]+)/);
  const projectId = projectMatch ? projectMatch[1] : null;
  const [project, setProject] = useState<ProjectInfoData | null>(null);

  const handleUpgradeClick = () => {
    navigate('/account/profile');
  };

  useEffect(() => {
    let isMounted = true;

    const fetchProject = async () => {
      if (!projectId) {
        setProject(null);
        return;
      }

      try {
        const projectData = await projectServices.getById(projectId);
        if (isMounted) {
          setProject(projectData);
        }
      } catch (error) {
        console.error('Error fetching project:', error);
      }
    };

    fetchProject();

    return () => {
      isMounted = false;
    };
  }, [projectId]);

  // Only show Project Navigation section if we're in a project
  if (item.label === 'Project Navigation' && !projectId) {
    return null;
  }

  // Filter menu items based on feature flags
  const filteredChildren = item.children.filter((child) => {
    if (child.label === 'Financial Insights') {
      return project && FEATURE_FLAG_EFFICIENCY_MEASURES(project?.attributes?.name || '');
    }
    return true;
  });

  return (
    <div className="w-full">
      <h4
        className={cn(
          'w-full px-4 py-2 text-xs font-medium uppercase text-muted-foreground',
          'transition-all duration-150',
          {
            'translate-x-0 opacity-100': sidebarOpen,
            hidden: !sidebarOpen,
          }
        )}
      >
        {item.label}
      </h4>
      {item.label === 'Project Navigation' && project && (
        <div className={cn('mb-2 px-4', { hidden: !sidebarOpen })}>
          <h3 className="truncate text-sm font-medium text-foreground">{project.attributes.name}</h3>
        </div>
      )}
      <div className="w-full">
        {filteredChildren.map((child) => {
          const featureKey = getFeatureKeyForNavItem(child.label);

          // If this menu item doesn't need feature gating, render it directly
          if (!featureKey) {
            return (
              <NavItem
                key={child.label}
                item={child}
                collapsed={!sidebarOpen}
                projectId={projectId || undefined}
                isMobile={isMobile}
              />
            );
          }

          const requiredPlan = getRequiredPlanForFeature(featureKey);

          // Otherwise, wrap it with FeatureGate
          return (
            <FeatureGate
              key={child.label}
              feature={featureKey}
              fallback={
                sidebarOpen ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div
                        className="flex w-full cursor-pointer items-center px-4 py-3 text-muted-foreground/50 hover:bg-primary/5"
                        onClick={handleUpgradeClick}
                      >
                        {child.icon && <div className="text-[1.5rem] text-muted-foreground/50">{child.icon}</div>}
                        <span className="ml-3 text-sm font-medium">{child.label}</span>
                        <LockIcon className="ml-2 h-3 w-3" />
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="right" className="ml-2 px-2 py-1 text-sm">
                      <p>Available on {requiredPlan} plan or higher</p>
                      <p className="text-xs text-muted-foreground">Click to upgrade</p>
                    </TooltipContent>
                  </Tooltip>
                ) : (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div
                        className="flex w-full cursor-pointer justify-center py-4 text-muted-foreground/50 hover:bg-primary/5"
                        onClick={handleUpgradeClick}
                      >
                        <div className="text-[1.5rem] text-muted-foreground/50">{child.icon}</div>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="right" className="ml-2 px-2 py-1 text-sm">
                      <div className="flex items-center gap-1">
                        <span>{child.label}</span>
                        <LockIcon className="h-3 w-3" />
                      </div>
                      <p className="text-xs text-muted-foreground">Available on {requiredPlan} plan</p>
                    </TooltipContent>
                  </Tooltip>
                )
              }
            >
              <NavItem item={child} collapsed={!sidebarOpen} projectId={projectId || undefined} isMobile={isMobile} />
            </FeatureGate>
          );
        })}
      </div>
    </div>
  );
};

const MenuTree = ({ items, isMobile }: { items: Menu; isMobile?: boolean }) => {
  return (
    <div className="flex w-full flex-col gap-6">
      {items.map((item, index) => (
        <SectionItem key={index} item={item} isMobile={isMobile} />
      ))}
    </div>
  );
};

export const VerticalNavbar = ({ items = [], isMobile }: { items: Menu; isMobile?: boolean }) => {
  const { sidebarOpen, toggleSidebar } = useAppLayout();
  const navigate = useNavigate();
  const { setAuthToken } = useJumboAuth();

  const handleLogout = (e: React.MouseEvent) => {
    e.preventDefault();
    setAuthToken(null);
    navigate('/user/login');
  };

  return (
    <div className="flex h-full flex-col justify-between">
      <div className="pt-6">
        <MenuTree items={items} isMobile={isMobile} />
      </div>

      <div
        className={cn('mt-auto border-t border-border bg-background p-2', 'flex flex-col gap-2', {
          'items-center': !sidebarOpen,
        })}
      >
        {/* Collapse/Expand Button - Only show on desktop */}
        {!isMobile && (
          <NavItem
            item={{
              uri: '#',
              label: sidebarOpen ? 'Collapse' : 'Expand',
              type: 'nav-item',
              icon: sidebarOpen ? (
                <PanelLeftCloseIcon className="h-5 w-5" />
              ) : (
                <PanelLeftOpenIcon className="h-5 w-5" />
              ),
              onClick: (e) => {
                e.preventDefault();
                toggleSidebar();
              },
            }}
            collapsed={!sidebarOpen}
            className="hover:bg-accent hover:text-accent-foreground"
          />
        )}

        {/* Logout Button */}
        <NavItem
          item={{
            uri: '#',
            label: 'Logout',
            type: 'nav-item',
            icon: <LogOutIcon className="h-5 w-5" />,
            onClick: handleLogout,
          }}
          collapsed={!sidebarOpen}
          isMobile={isMobile}
          className="hover:bg-accent hover:text-accent-foreground"
        />
      </div>
    </div>
  );
};
